import { callable } from './callable.js'

// ---------------------------------------------------------------------------
// 1. Fee Settings
// ---------------------------------------------------------------------------

export interface EduFeeSettings {
  dutyUids: string[]
  receiptPrefix: string
  fyStartMonth: number
  taxId: string | null
  reminderDaysBefore: number
}

export interface GetEduFeeSettingsInput {
  workspaceId: string
}

export interface GetEduFeeSettingsResult {
  settings: EduFeeSettings
}

export interface SetEduFeeSettingsInput {
  workspaceId: string
  dutyUids?: string[]
  receiptPrefix?: string
  fyStartMonth?: number
  taxId?: string | null
  reminderDaysBefore?: number
}

export interface SetEduFeeSettingsResult {
  settings: EduFeeSettings
}

export async function getEduFeeSettings(
  input: GetEduFeeSettingsInput,
): Promise<GetEduFeeSettingsResult> {
  return callable<GetEduFeeSettingsInput, GetEduFeeSettingsResult>(
    'getEduFeeSettings',
    input,
  )
}

export async function setEduFeeSettings(
  input: SetEduFeeSettingsInput,
): Promise<SetEduFeeSettingsResult> {
  return callable<SetEduFeeSettingsInput, SetEduFeeSettingsResult>(
    'setEduFeeSettings',
    input,
  )
}

// ---------------------------------------------------------------------------
// 2. Fee Plans
// ---------------------------------------------------------------------------

export type FeePlanKind = 'FEE' | 'PACK' | 'MEMBERSHIP'
export type FeeScheduleType = 'ONE_TIME' | 'MONTHLY' | 'TERM' | 'YEARLY' | 'CUSTOM'
export type LateFeeType = 'NONE' | 'FLAT' | 'PERCENT'

export interface FeeItem {
  id: string
  label: string
  amountMinor: number
}

export interface CustomInstalment {
  id: string
  label?: string
  dueDate: string
  amountMinor: number
}

export interface FeeSchedule {
  type: FeeScheduleType
  startDate?: string
  endDate?: string
  dayOfMonth?: number
  dueDay?: number
  termIds?: string[]
  dueMonth?: number
  instalments?: CustomInstalment[]
}

export interface LateFeeConfig {
  type: LateFeeType
  value?: number
  amountMinor?: number
  ratePercent?: number
  graceDays: number
}

export interface PackConfig {
  sessions?: number
  totalSessions?: number
  validityDays: number
}

export interface MembershipConfig {
  periodMonths: number
}

export interface EduFeePlanInput {
  workspaceId: string
  rootId: string
  nodeIds: string[]
  name: string
  kind: FeePlanKind
  items: FeeItem[]
  schedule: FeeSchedule
  lateFee: LateFeeConfig
  pack?: PackConfig
  membership?: MembershipConfig
  startDate?: string
  endDate?: string
}

export interface EduFeePlanData extends EduFeePlanInput {
  id: string
  version: number
  status?: 'ACTIVE' | 'ARCHIVED'
  archived?: boolean
  totalAmountMinor?: number
  readAccess?: string[]
  createdBy?: string
  updatedBy?: string
  createdAt?: unknown
  updatedAt?: unknown
}

export interface CreateEduFeePlanResult {
  planId: string
  plan: EduFeePlanData
}

export interface UpdateEduFeePlanInput extends Partial<EduFeePlanInput> {
  workspaceId: string
  planId: string
  expectedVersion: number
}

export interface ArchiveEduFeePlanInput {
  workspaceId: string
  planId: string
  expectedVersion: number
  archived?: boolean
}

export interface ListEduFeePlansInput {
  workspaceId: string
  rootId?: string
  status?: 'ACTIVE' | 'ARCHIVED'
}

export interface ListEduFeePlansResult {
  plans: EduFeePlanData[]
}

export interface GetEduFeePlanInput {
  workspaceId: string
  planId: string
}

export interface GetEduFeePlanResult {
  plan: EduFeePlanData
}

export async function createEduFeePlan(
  input: EduFeePlanInput,
): Promise<CreateEduFeePlanResult> {
  return callable<EduFeePlanInput, CreateEduFeePlanResult>(
    'createEduFeePlan',
    input,
  )
}

export async function updateEduFeePlan(
  input: UpdateEduFeePlanInput,
): Promise<{ plan: EduFeePlanData }> {
  return callable<UpdateEduFeePlanInput, { plan: EduFeePlanData }>(
    'updateEduFeePlan',
    input,
  )
}

export async function archiveEduFeePlan(
  input: ArchiveEduFeePlanInput,
): Promise<{ plan: EduFeePlanData }> {
  return callable<ArchiveEduFeePlanInput, { plan: EduFeePlanData }>(
    'archiveEduFeePlan',
    input,
  )
}

export async function listEduFeePlans(
  input: ListEduFeePlansInput,
): Promise<ListEduFeePlansResult> {
  return callable<ListEduFeePlansInput, ListEduFeePlansResult>(
    'listEduFeePlans',
    input,
  )
}

export async function getEduFeePlan(
  input: GetEduFeePlanInput,
): Promise<GetEduFeePlanResult> {
  return callable<GetEduFeePlanInput, GetEduFeePlanResult>(
    'getEduFeePlan',
    input,
  )
}

// ---------------------------------------------------------------------------
// 3. Apply / Assign / Unassign
// ---------------------------------------------------------------------------

export interface ApplyEduFeePlanInput {
  workspaceId: string
  planId: string
}

export interface ApplyEduFeePlanResult {
  assignedCount: number
  createdDuesCount: number
}

export async function applyEduFeePlan(
  input: ApplyEduFeePlanInput,
): Promise<ApplyEduFeePlanResult> {
  return callable<ApplyEduFeePlanInput, ApplyEduFeePlanResult>(
    'applyEduFeePlan',
    input,
  )
}

export interface AssignEduFeePlanInput {
  workspaceId: string
  planId: string
  learnerId: string
  startDate?: string
}

export interface AssignEduFeePlanResult {
  assignmentId: string
  createdDuesCount: number
}

export async function assignEduFeePlan(
  input: AssignEduFeePlanInput,
): Promise<AssignEduFeePlanResult> {
  return callable<AssignEduFeePlanInput, AssignEduFeePlanResult>(
    'assignEduFeePlan',
    input,
  )
}

export interface UnassignEduFeePlanInput {
  workspaceId: string
  planId: string
  learnerId: string
}

export interface UnassignEduFeePlanResult {
  success: boolean
  cancelledDuesCount: number
}

export async function unassignEduFeePlan(
  input: UnassignEduFeePlanInput,
): Promise<UnassignEduFeePlanResult> {
  return callable<UnassignEduFeePlanInput, UnassignEduFeePlanResult>(
    'unassignEduFeePlan',
    input,
  )
}

// ---------------------------------------------------------------------------
// 4. Adjustments
// ---------------------------------------------------------------------------

export type AdjustmentType =
  | 'DISCOUNT_FLAT'
  | 'DISCOUNT_PERCENT'
  | 'SCHOLARSHIP_FLAT'
  | 'SCHOLARSHIP_PERCENT'
  | 'WAIVER'

export interface EduFeeAdjustment {
  id: string
  type: AdjustmentType
  value: number
  itemIds?: string[]
  reason: string
  approvedBy: string
  approvedAt: string
}

export interface SetEduFeeAdjustmentInput {
  workspaceId: string
  planId: string
  learnerId: string
  type: AdjustmentType
  value: number
  itemIds?: string[]
  reason: string
  adjustmentId?: string
}

export interface SetEduFeeAdjustmentResult {
  adjustment: EduFeeAdjustment
}

export async function setEduFeeAdjustment(
  input: SetEduFeeAdjustmentInput,
): Promise<SetEduFeeAdjustmentResult> {
  return callable<SetEduFeeAdjustmentInput, SetEduFeeAdjustmentResult>(
    'setEduFeeAdjustment',
    input,
  )
}

export interface RemoveEduFeeAdjustmentInput {
  workspaceId: string
  planId: string
  learnerId: string
  adjustmentId: string
}

export interface RemoveEduFeeAdjustmentResult {
  success: boolean
}

export async function removeEduFeeAdjustment(
  input: RemoveEduFeeAdjustmentInput,
): Promise<RemoveEduFeeAdjustmentResult> {
  return callable<RemoveEduFeeAdjustmentInput, RemoveEduFeeAdjustmentResult>(
    'removeEduFeeAdjustment',
    input,
  )
}

// ---------------------------------------------------------------------------
// 5. Dues
// ---------------------------------------------------------------------------

export type EduDueStatus =
  | 'UPCOMING'
  | 'DUE'
  | 'PARTIAL'
  | 'PAID'
  | 'OVERDUE'
  | 'WAIVED'
  | 'CANCELLED'

export interface EduDueHistoryEntry {
  type?: string
  action?: string
  adjustmentId?: string
  adjustmentType?: string
  amountMinor?: number
  reason?: string
  approvedBy?: string
  approvedAt?: string
  actorUid?: string
  timestamp?: string
  [key: string]: unknown
}

export interface EduDueData {
  id: string
  workspaceId: string
  rootId: string
  nodeId: string
  planId: string
  learnerId: string
  title: string
  label?: string
  dueDate: string
  baseMinor: number
  adjustmentMinor: number
  lateFeeMinor: number
  paidMinor: number
  balanceMinor: number
  status: EduDueStatus
  isOverdue?: boolean
  learnerName?: string
  nodeName?: string
  className?: string
  planName?: string
  history?: EduDueHistoryEntry[]
  teacherUids?: string[]
  createdAt?: unknown
  updatedAt?: unknown
}

export interface ListEduDuesInput {
  workspaceId: string
  nodeId?: string
  learnerId?: string
  status?: string
  from?: string
  to?: string
  overdueOnly?: boolean
}

export interface ListEduDuesResult {
  dues: EduDueData[]
}

export async function listEduDues(
  input: ListEduDuesInput,
): Promise<ListEduDuesResult> {
  return callable<ListEduDuesInput, ListEduDuesResult>(
    'listEduDues',
    input,
  )
}

// ---------------------------------------------------------------------------
// 6. Payments, Receipts, Void & Family Reads (a2 Callables for b2 screens)
// ---------------------------------------------------------------------------

export interface RecordEduFeePaymentInput {
  workspaceId: string
  learnerId: string
  allocations: Array<{ dueId: string; amountMinor: number }>
  method: 'CASH' | 'UPI' | 'BANK'
  reference?: string
  paidOn: string
  note?: string
  requestId?: string
}

export interface RecordEduFeePaymentResult {
  paymentId: string
  receiptNo: string
  payment: Record<string, unknown>
}

export async function recordEduFeePayment(
  input: RecordEduFeePaymentInput,
): Promise<RecordEduFeePaymentResult> {
  return callable<RecordEduFeePaymentInput, RecordEduFeePaymentResult>(
    'recordEduFeePayment',
    input,
  )
}

export interface EduFeePaymentItem {
  id: string
  receiptNo: string
  learnerId: string
  learnerName?: string
  nodeId: string | null
  totalMinor: number
  method: 'CASH' | 'UPI' | 'BANK'
  reference: string | null
  paidOn: string
  receivedBy: string
  receivedByName?: string
  status: 'RECORDED' | 'VOIDED'
  pdfStatus: 'PENDING' | 'READY' | 'FAILED'
  voidReason: string | null
  voidedAt: string | null
}

export interface ListEduFeePaymentsInput {
  workspaceId: string
  learnerId?: string
  nodeId?: string
  status?: 'RECORDED' | 'VOIDED'
  method?: 'CASH' | 'UPI' | 'BANK'
  from?: string
  to?: string
  limit?: number
  cursor?: string | null
}

export interface ListEduFeePaymentsResult {
  payments: EduFeePaymentItem[]
  nextCursor: string | null
}

export async function listEduFeePayments(
  input: ListEduFeePaymentsInput,
): Promise<ListEduFeePaymentsResult> {
  return callable<ListEduFeePaymentsInput, ListEduFeePaymentsResult>(
    'listEduFeePayments',
    input,
  )
}

export interface VoidEduFeeReceiptInput {
  workspaceId: string
  paymentId: string
  reason: string
}

export interface VoidEduFeeReceiptResult {
  payment: Record<string, unknown>
}

export async function voidEduFeeReceipt(
  input: VoidEduFeeReceiptInput,
): Promise<VoidEduFeeReceiptResult> {
  return callable<VoidEduFeeReceiptInput, VoidEduFeeReceiptResult>(
    'voidEduFeeReceipt',
    input,
  )
}

export interface GetEduFeeReceiptPdfUrlInput {
  workspaceId: string
  paymentId: string
}

export interface GetEduFeeReceiptPdfUrlResult {
  signedUrl: string
}

export async function getEduFeeReceiptPdfUrl(
  input: GetEduFeeReceiptPdfUrlInput,
): Promise<GetEduFeeReceiptPdfUrlResult> {
  return callable<GetEduFeeReceiptPdfUrlInput, GetEduFeeReceiptPdfUrlResult>(
    'getEduFeeReceiptPdfUrl',
    input,
  )
}

export interface FamilyFeeDueItem {
  id: string
  planId: string
  planName?: string
  dueDate: string
  baseMinor: number
  adjustmentMinor: number
  lateFeeMinor: number
  paidMinor: number
  balanceMinor: number
  status: EduDueStatus
  isOverdue?: boolean
}

export interface FamilyFeePaymentItem {
  id: string
  receiptNo: string
  totalMinor: number
  method: 'CASH' | 'UPI' | 'BANK'
  reference: string | null
  paidOn: string
  status: 'RECORDED' | 'VOIDED'
  pdfStatus: 'PENDING' | 'READY' | 'FAILED'
  voidReason: string | null
}

export interface ListFamilyFeesInput {
  workspaceId: string
  learnerId: string
}

export interface ListFamilyFeesResult {
  currency: string | null
  payOnline: { available: false }
  dues: {
    upcoming: FamilyFeeDueItem[]
    overdue: FamilyFeeDueItem[]
    paid: FamilyFeeDueItem[]
  }
  payments: FamilyFeePaymentItem[]
}

export async function listFamilyFees(
  input: ListFamilyFeesInput,
): Promise<ListFamilyFeesResult> {
  return callable<ListFamilyFeesInput, ListFamilyFeesResult>(
    'listFamilyFees',
    input,
  )
}

export interface GetFamilyFeeReceiptPdfUrlInput {
  workspaceId: string
  paymentId: string
}

export interface GetFamilyFeeReceiptPdfUrlResult {
  signedUrl: string
}

export async function getFamilyFeeReceiptPdfUrl(
  input: GetFamilyFeeReceiptPdfUrlInput,
): Promise<GetFamilyFeeReceiptPdfUrlResult> {
  return callable<GetFamilyFeeReceiptPdfUrlInput, GetFamilyFeeReceiptPdfUrlResult>(
    'getFamilyFeeReceiptPdfUrl',
    input,
  )
}
